/**
 * Native touch drag-and-drop for record table row reorder (#997 / #999).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PanResponder, Platform, View } from 'react-native';

import { useRecordTableDragScrollLock } from './RecordTableDragScrollLock';

import type { RefObject } from 'react';

export interface RecordTableRowBounds {
  top: number;
  bottom: number;
}

export function resolveRecordTableReorderActive(params: {
  readOnly?: boolean;
  /** Devices view: allow drag while tables stay menu/add read-only (Story 3.7). */
  allowReorder?: boolean;
  onReorderSection?: unknown;
  itemCount: number;
}): boolean {
  const blockedByReadOnly = Boolean(params.readOnly) && !params.allowReorder;
  return (
    !blockedByReadOnly &&
    Boolean(params.onReorderSection) &&
    params.itemCount > 1
  );
}

export function useNativeDragHandle(
  enabled: boolean,
  rowIndex: number,
  onDragStart: (index: number) => void,
  onDragMove: (pageY: number) => void,
  onDragEnd: () => void,
): Record<string, unknown> {
  const onDragStartRef = useRef(onDragStart);
  const onDragMoveRef = useRef(onDragMove);
  const onDragEndRef = useRef(onDragEnd);

  onDragStartRef.current = onDragStart;
  onDragMoveRef.current = onDragMove;
  onDragEndRef.current = onDragEnd;

  return useMemo(() => {
    if (Platform.OS === 'web' || !enabled) {
      return {};
    }

    const panResponder = PanResponder.create({
      // Capture early so the parent KeyboardAwareScrollView does not steal the
      // gesture on Devices / step lists (iOS + Android).
      onStartShouldSetPanResponderCapture: () => true,
      onMoveShouldSetPanResponderCapture: (_e, gestureState) =>
        Math.abs(gestureState.dy) > 2 || Math.abs(gestureState.dx) > 2,
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: () => {
        onDragStartRef.current(rowIndex);
      },
      onPanResponderMove: event => {
        onDragMoveRef.current(event.nativeEvent.pageY);
      },
      onPanResponderRelease: () => {
        onDragEndRef.current();
      },
      onPanResponderTerminate: () => {
        onDragEndRef.current();
      },
    });

    return panResponder?.panHandlers ?? {};
  }, [enabled, rowIndex]);
}

export function useRecordTableRowLayout(
  dropRowRef: RefObject<View | null>,
  enabled: boolean,
  rowIndex: number,
  onRowLayout?: (index: number, bounds: RecordTableRowBounds) => void,
  registerRowRemeasure?: (
    index: number,
    remeasure: (() => void) | null,
  ) => void,
): () => void {
  const remeasure = useCallback(() => {
    if (Platform.OS === 'web' || !enabled || !onRowLayout) {
      return;
    }
    dropRowRef.current?.measureInWindow((_x, y, _width, height) => {
      onRowLayout(rowIndex, { top: y, bottom: y + height });
    });
  }, [dropRowRef, enabled, onRowLayout, rowIndex]);

  // Keep a live remasure hook so drag-start can refresh after parent scroll.
  useEffect(() => {
    if (Platform.OS === 'web' || !enabled || !registerRowRemeasure) {
      return;
    }
    registerRowRemeasure(rowIndex, remeasure);
    return () => {
      registerRowRemeasure(rowIndex, null);
    };
  }, [enabled, registerRowRemeasure, remeasure, rowIndex]);

  return remeasure;
}

export function useRecordTableSectionReorder<Section>({
  section,
  onReorderSection,
  canReorderTo,
}: {
  section: Section;
  onReorderSection?: (
    section: Section,
    fromIndex: number,
    toIndex: number,
  ) => void;
  /**
   * Optional guard: return false to forbid moving the row at `fromIndex` onto
   * `toIndex`. Used by the flat Devices tables to keep a drag inside its own
   * EXISTING/NEW status block so a NEW ↔ EXISTING move can never land (#999).
   * When omitted, every target is allowed (grouped step sections, web).
   */
  canReorderTo?: (fromIndex: number, toIndex: number) => boolean;
}) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const draggedIndexRef = useRef<number | null>(null);
  const dragOverIndexRef = useRef<number | null>(null);
  const canReorderToRef = useRef(canReorderTo);
  canReorderToRef.current = canReorderTo;

  const isReorderTargetAllowed = useCallback(
    (fromIndex: number | null, toIndex: number | null): boolean => {
      if (fromIndex == null || toIndex == null) {
        return false;
      }
      const guard = canReorderToRef.current;
      return guard ? guard(fromIndex, toIndex) : true;
    },
    [],
  );
  const rowLayoutsRef = useRef<Map<number, RecordTableRowBounds>>(new Map());
  const remeasureFnsRef = useRef<Map<number, () => void>>(new Map());
  const { beginTableDrag, endTableDrag } = useRecordTableDragScrollLock();
  const dragScrollActiveRef = useRef(false);

  const registerRowLayout = useCallback(
    (rowIndex: number, bounds: RecordTableRowBounds) => {
      rowLayoutsRef.current.set(rowIndex, bounds);
    },
    [],
  );

  const registerRowRemeasure = useCallback(
    (rowIndex: number, remeasure: (() => void) | null) => {
      if (remeasure == null) {
        remeasureFnsRef.current.delete(rowIndex);
        return;
      }
      remeasureFnsRef.current.set(rowIndex, remeasure);
    },
    [],
  );

  const remeasureAllRows = useCallback(() => {
    for (const remeasure of remeasureFnsRef.current.values()) {
      remeasure();
    }
  }, []);

  const resolveRowAtPageY = useCallback(
    (pageY: number, excludeIndex: number | null): number | null => {
      let match: number | null = null;
      for (const [index, bounds] of rowLayoutsRef.current.entries()) {
        if (index === excludeIndex) {
          continue;
        }
        if (pageY >= bounds.top && pageY <= bounds.bottom) {
          match = index;
        }
      }
      return match;
    },
    [],
  );

  const handleDragHandleStart = useCallback(
    (rowIndex: number) => {
      // Fresh window coords after parent scroll (Devices / step native drag).
      remeasureAllRows();
      if (!dragScrollActiveRef.current) {
        dragScrollActiveRef.current = true;
        beginTableDrag();
      }
      draggedIndexRef.current = rowIndex;
      setDraggedIndex(rowIndex);
    },
    [beginTableDrag, remeasureAllRows],
  );

  const handleDragHandleMove = useCallback(
    (pageY: number) => {
      const currentDragged = draggedIndexRef.current;
      if (currentDragged == null) {
        return;
      }
      const over = resolveRowAtPageY(pageY, currentDragged);
      // Forbidden target (e.g. the other status block on a flat Devices table):
      // leave the drop indicator where it was so the row cannot cross over.
      if (over != null && !isReorderTargetAllowed(currentDragged, over)) {
        return;
      }
      dragOverIndexRef.current = over;
      setDragOverIndex(over);
    },
    [resolveRowAtPageY, isReorderTargetAllowed],
  );

  const finalizeReorder = useCallback(
    (fromIndex: number | null, toIndex: number | null) => {
      if (
        fromIndex != null &&
        toIndex != null &&
        fromIndex !== toIndex &&
        isReorderTargetAllowed(fromIndex, toIndex)
      ) {
        onReorderSection?.(section, fromIndex, toIndex);
      }
      draggedIndexRef.current = null;
      dragOverIndexRef.current = null;
      setDraggedIndex(null);
      setDragOverIndex(null);
      if (dragScrollActiveRef.current) {
        dragScrollActiveRef.current = false;
        endTableDrag();
      }
    },
    [endTableDrag, onReorderSection, section, isReorderTargetAllowed],
  );

  const handleDragHandleEnd = useCallback(() => {
    finalizeReorder(draggedIndexRef.current, dragOverIndexRef.current);
  }, [finalizeReorder]);

  const handleRowDragOver = useCallback(
    (rowIndex: number) => {
      const dragged = draggedIndexRef.current;
      if (
        dragged != null &&
        dragged !== rowIndex &&
        isReorderTargetAllowed(dragged, rowIndex)
      ) {
        dragOverIndexRef.current = rowIndex;
        setDragOverIndex(rowIndex);
      }
    },
    [isReorderTargetAllowed],
  );

  const handleRowDrop = useCallback(
    (rowIndex: number) => {
      finalizeReorder(draggedIndexRef.current, rowIndex);
    },
    [finalizeReorder],
  );

  return {
    draggedIndex,
    dragOverIndex,
    registerRowLayout,
    registerRowRemeasure,
    handleDragHandleStart,
    handleDragHandleMove,
    handleDragHandleEnd,
    handleRowDragOver,
    handleRowDrop,
  };
}
